import { ReminderStatusBadge } from '@/components/badges/ReminderStatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { homeTaskSubscriptionsListState, taskTypesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatDate, formatRelative } from '@/util/dates';
import logger from '@/util/logger';
import { HomeTaskSubscriptionDao } from '@rock/db-admin';
import { formatInterval } from '@rock/tasks';
import type { HomeId, HomeTaskSubscription } from '@rock/types';
import { todayLocalDate } from '@rock/util';
import { type FC, useMemo, useState } from 'react';
import { toast } from 'sonner';

const log = logger('HomeRemindersTab');
const subscriptionDao = new HomeTaskSubscriptionDao();

/**
 * All task-subscriptions ("reminders") for a home: what's scheduled, on what cadence, and when the
 * next task is due. "Move to Now" pulls a subscription's nextTaskDate to today so the hourly task
 * generator picks it up — promoted here from the per-consideration drill-in.
 */
export const HomeRemindersTab: FC<{ homeId: HomeId }> = ({ homeId }) => {
    const [subscriptions, subscriptionsLoadable] = useLoadableAtomValue(
        useMemo(() => homeTaskSubscriptionsListState(homeId), [homeId]),
        [],
    );
    const [taskTypes] = useLoadableAtomValue(taskTypesState, []);
    const taskTypeTitleById = useMemo(() => {
        const map = new Map<string, string>();
        for (const taskType of taskTypes) {
            map.set(taskType.id, taskType.title);
        }
        return map;
    }, [taskTypes]);

    return (
        <Card>
            <CardHeader>
                <CardTitle>Reminders</CardTitle>
                <CardDescription>
                    Task subscriptions for this home — cadence, status, and next scheduled task.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {subscriptions.length === 0 ? (
                    <div className="section-description">
                        {subscriptionsLoadable.state === 'loading'
                            ? 'Loading reminders…'
                            : subscriptionsLoadable.state === 'hasError'
                              ? 'Couldn’t load reminders.'
                              : 'No reminders set up yet.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Task</TableHead>
                                <TableHead>Cadence</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Next task</TableHead>
                                <TableHead>Last performed</TableHead>
                                <TableHead className="w-[120px]" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {subscriptions.map((subscription) => (
                                <ReminderRow
                                    key={subscription.id}
                                    homeId={homeId}
                                    subscription={subscription}
                                    title={taskTypeTitleById.get(subscription.taskTypeId) ?? subscription.taskTypeId}
                                />
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};

const ReminderRow: FC<{ homeId: HomeId; subscription: HomeTaskSubscription; title: string }> = ({
    homeId,
    subscription,
    title,
}) => {
    const onRowClick = useRowClick();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isMoving, setIsMoving] = useState(false);

    // The per-consideration drill-in has the full subscription/task history; custom reminders
    // (no considerationTypeId) have no drill-in, so their rows aren't clickable.
    const drillIn = subscription.considerationTypeId
        ? `/directory/homes/${homeId}/considerations/${subscription.considerationTypeId}/tasks/${subscription.taskTypeId}`
        : undefined;

    const cadence =
        subscription.subscription.type === 'recurring'
            ? `Every ${formatInterval(subscription.subscription.interval)}`
            : 'One-time';

    const handleMoveToNow = async () => {
        setIsMoving(true);
        try {
            const today = todayLocalDate();
            await subscriptionDao.updateHomeTaskSubscription(homeId, subscription.id, { nextTaskDate: today });
            toast.success('Next Task Date Updated', {
                description:
                    'Moved to now. Task generation runs hourly, so the task may take a little while to appear.',
            });
            log.info('Moved subscription nextTaskDate to now', { homeId, subscriptionId: subscription.id, today });
            setConfirmOpen(false);
        } catch (err) {
            log.error('Failed to move nextTaskDate to now', { err, homeId, subscriptionId: subscription.id });
            toast.error('Unable to update next task date', {
                description: err instanceof Error ? err.message : 'Unknown error',
            });
        } finally {
            setIsMoving(false);
        }
    };

    return (
        <TableRow
            className={drillIn ? 'cursor-pointer transition-colors hover:bg-muted/50' : undefined}
            onClick={drillIn ? onRowClick(drillIn) : undefined}
        >
            <TableCell className="font-medium">{title}</TableCell>
            <TableCell>{cadence}</TableCell>
            <TableCell>
                <ReminderStatusBadge status={subscription.status} />
            </TableCell>
            <TableCell>{subscription.nextTaskDate ? formatDate(subscription.nextTaskDate) : '—'}</TableCell>
            <TableCell className="text-muted-foreground">
                {subscription.lastPerformedAt ? formatRelative(subscription.lastPerformedAt) : '—'}
            </TableCell>
            {/* Cell-level stopPropagation so the action doesn't trigger the row drill-in. */}
            <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                {subscription.status === 'active' && subscription.nextTaskDate && (
                    <>
                        <Button variant="outline" size="sm" onClick={() => setConfirmOpen(true)}>
                            Move to Now
                        </Button>
                        <Dialog open={confirmOpen} onOpenChange={(open) => !isMoving && setConfirmOpen(open)}>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Move next task to now?</DialogTitle>
                                    <DialogDescription>
                                        Sets the next task date for "{title}" to today (currently{' '}
                                        {formatDate(subscription.nextTaskDate)}). The hourly generator will create the
                                        task on its next run.
                                    </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isMoving}>
                                        Cancel
                                    </Button>
                                    <Button onClick={handleMoveToNow} disabled={isMoving}>
                                        {isMoving ? 'Moving…' : 'Move to Now'}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </>
                )}
            </TableCell>
        </TableRow>
    );
};
